/**
 * Centralized Data Normalization Utilities
 * 
 * This module provides single source of truth for data normalization
 * across frontend components, eliminating duplicate normalization logic.
 */

export interface NormalizedOrder {
  id: string;
  order_number?: string;
  status: string;
  status_raw?: string;
  order_type?: string | null;
  garmentType?: string | null;
  customer_name: string;
  customer_email?: string;
  created_date?: string;
  created_at?: string;
  last_updated?: string;
  updated_at?: string;
  amount?: number | null;
  price?: number | null;
  cloth_type?: string | null;
  measurements: Record<string, any>;
  photos: any[];
  due_date?: string;
  pickup_date?: string;
  priority?: 'high' | 'medium' | 'low';
  special_notes?: string;
  assigned_tailor?: string;
  cutting_started?: boolean;
  stitching_started?: boolean;
  fabric_selected?: boolean;
  quality_check_completed?: boolean;
  ready_for_pickup?: boolean;
  invoice?: {
    id: number;
    total: number;
    material_component?: number;
    stitching_component?: number;
    issued_at?: string;
    created_at?: string;
  };
}

/**
 * Normalize order data to ensure consistent field names and values.
 * 
 * This handles variations in field names (e.g., order_type vs garmentType)
 * and ensures all required fields are present with sensible defaults.
 * 
 * @param orderData - Raw order data from API/DB
 * @returns Normalized order data
 */
export function normalizeOrderData(orderData: any): NormalizedOrder {
  // Extract cloth_type from notes/description if not directly provided
  let cloth_type = orderData.cloth_type;
  if (!cloth_type && orderData.description) {
    const match = orderData.description.match(/[Ff]abric\/[Cc]loth:\s*(.+?)(?:\.|$)/);
    if (match) {
      cloth_type = match[1].trim();
    }
  }

  // Handle measurements - support both legacy format and DTO format
  let measurements: Record<string, any> = {};
  
  // Legacy format: measurements is a flat dictionary
  if (orderData.measurements && typeof orderData.measurements === 'object' && !Array.isArray(orderData.measurements)) {
    measurements = orderData.measurements;
  } 
  // DTO format: measurementSet.fieldsPreview is an array that needs to be converted to flat dict
  else if (orderData.measurementSet?.fieldsPreview && Array.isArray(orderData.measurementSet.fieldsPreview)) {
    const fieldsPreview = orderData.measurementSet.fieldsPreview;
    for (const field of fieldsPreview) {
      if (field.key && field.value !== undefined && field.value !== null && field.value !== '') {
        // Convert string value to number if possible, otherwise keep as string
        const numValue = parseFloat(field.value);
        measurements[field.key] = isNaN(numValue) ? field.value : numValue;
      }
    }
  }

  // Handle photos - support both formats
  let photos: any[] = [];
  if (Array.isArray(orderData.photos)) {
    photos = orderData.photos;
  }

  // Extract assigned_tailor - support both formats
  let assigned_tailor: string | undefined;
  if (orderData.assigned_tailor) {
    assigned_tailor = orderData.assigned_tailor;
  } else if (orderData.assignedTailor?.name) {
    assigned_tailor = orderData.assignedTailor.name;
  } else if (orderData.tailor?.name) {
    assigned_tailor = orderData.tailor.name;
  }

  return {
    id: orderData.id?.toString() || '',
    order_number: orderData.order_number || orderData.id?.toString(),
    status: (orderData.status_raw || orderData.status || 'pending').toLowerCase(),
    status_raw: orderData.status_raw || orderData.status,
    order_type: orderData.order_type || orderData.garmentType || null,
    garmentType: orderData.garmentType || orderData.order_type || null,
    customer_name: orderData.customer_name || orderData.customer?.name || 'Not Available',
    customer_email: orderData.customer_email || orderData.customer?.email,
    created_date: orderData.created_date || orderData.created_at,
    created_at: orderData.created_at || orderData.created_date,
    last_updated: orderData.last_updated || orderData.updated_at,
    updated_at: orderData.updated_at || orderData.last_updated,
    amount: orderData.amount ?? orderData.price ?? null,
    price: orderData.price ?? orderData.amount ?? null,
    cloth_type: cloth_type || null,
    measurements: measurements,
    photos: photos,
    due_date: orderData.due_date || orderData.dueAt,
    pickup_date: orderData.pickup_date,
    priority: (orderData.priority as 'high' | 'medium' | 'low') || 'medium',
    special_notes: orderData.special_notes || orderData.notes?.customerNote || orderData.notes,
    assigned_tailor: assigned_tailor,
    cutting_started: orderData.cutting_started || false,
    stitching_started: orderData.stitching_started || false,
    fabric_selected: orderData.fabric_selected || false,
    quality_check_completed: orderData.quality_check_completed || false,
    ready_for_pickup: orderData.ready_for_pickup || false,
    invoice: orderData.invoice,
  };
}

/**
 * Normalize customer data to ensure consistent structure.
 * 
 * @param customerData - Raw customer data from API/DB
 * @returns Normalized customer data
 */
export function normalizeCustomerData(customerData: any): {
  id?: number | string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
} {
  return {
    id: customerData.id,
    name: customerData.name || customerData.full_name,
    email: customerData.email,
    phone: customerData.phone || customerData.phone_number,
    address: customerData.address || 
      [customerData.address_line1, customerData.address_line2]
        .filter(Boolean)
        .join(' ')
        .trim() || undefined,
  };
}

/**
 * Normalize status string to consistent format.
 * 
 * @param status - Raw status value
 * @returns Normalized status string
 */
export function normalizeStatus(status: string | undefined | null): string {
  if (!status) return 'pending';
  return status.toLowerCase().trim();
}

/**
 * Format status for display (capitalize first letter).
 * 
 * @param status - Normalized status
 * @returns Formatted status for UI
 */
export function formatStatusForDisplay(status: string): string {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'pending':
      return 'Pending';
    case 'in_progress':
    case 'in progress':
      return 'In Progress';
    case 'ready':
    case 'ready for pickup':
      return 'Ready for Pickup';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      // Return capitalized version if it's already a display string
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

